import { useCallback, useEffect, useRef } from "react";
import { AgentJobDto } from "types/api/agentJobs";
import { isAbortError, PollOutcome } from "utils/agentJob";
import { AgentFailure, describeAgentFailure } from "utils/agentJobPhase";
import {
  getResponseError,
  toastResponseError,
} from "utils/responseMessageHelper";

// What a run reports back; every AgentEvent<TResult> dispatcher accepts these.
export type AgentRunEvent =
  | { type: "JOB_CREATED"; jobId: number }
  | { type: "JOB_CREATE_FAILED" }
  | { type: "JOB_FAILED"; failure: AgentFailure }
  | { type: "POLL_TIMED_OUT" }
  | { type: "KEEP_WAITING" };

type Options<T extends AgentJobDto, S extends string> = {
  // Polls one job until a terminal status (pollAgentJob or a specialisation).
  poll: (jobId: number, signal: AbortSignal) => Promise<PollOutcome<T, S>>;
  // False while the owning panel is closed: results that land then are
  // dropped and the poll is aborted. Unmount always counts as inactive.
  isActive?: boolean;
  dispatch: (event: AgentRunEvent) => void;
  // Terminal success (any terminal status but `failed`). The caller dispatches
  // JOB_DONE itself. Return the promise of any follow-up request: the run stays
  // abortable until it settles, so `signal` tells it when the panel closed.
  onDone: (job: T, signal: AbortSignal) => void | Promise<void>;
};

// Drives one agent job from creation to its terminal status: create →
// JOB_CREATED → poll → onDone | JOB_FAILED | POLL_TIMED_OUT. One AbortController
// per poll run and nothing outlives the panel; the phase transitions stay in
// the caller's reducer (utils/agentJobPhase).
export const useAgentJobRun = <T extends AgentJobDto, S extends string>({
  poll,
  isActive = true,
  dispatch,
  onDone,
}: Options<T, S>) => {
  const pollRef = useRef<AbortController | null>(null);
  const activeRef = useRef(isActive);
  // Callers pass fresh closures every render; a run reads the latest ones.
  const latest = useRef({ poll, dispatch, onDone });
  latest.current = { poll, dispatch, onDone };

  const abort = useCallback(() => {
    pollRef.current?.abort();
    pollRef.current = null;
  }, []);

  // For leaving the panel by switching to another one: it stays mounted (and
  // active) while the drawer content fades, so the caller stops the run here.
  // A job created after this is not polled.
  const stop = useCallback(() => {
    activeRef.current = false;
    abort();
  }, [abort]);

  useEffect(() => {
    activeRef.current = isActive;
    if (!isActive) abort();
  }, [isActive, abort]);

  useEffect(
    () => () => {
      activeRef.current = false;
      abort();
    },
    [abort],
  );

  const runPoll = useCallback(
    (jobId: number) => {
      abort();
      const controller = new AbortController();
      pollRef.current = controller;

      latest.current
        .poll(jobId, controller.signal)
        .then((outcome) => {
          if (controller.signal.aborted) return;
          // A generic `kind` cannot narrow the union; the timeout outcome has no job.
          if (!("job" in outcome)) {
            latest.current.dispatch({ type: "POLL_TIMED_OUT" });
            return;
          }
          if (outcome.kind === "failed") {
            latest.current.dispatch({
              type: "JOB_FAILED",
              failure: describeAgentFailure(outcome.job.error_payload),
            });
            return;
          }
          return latest.current.onDone(outcome.job, controller.signal);
        })
        .catch((error) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          latest.current.dispatch({
            type: "JOB_FAILED",
            failure: { kind: "generic", detail: getResponseError(error) },
          });
        })
        .finally(() => {
          if (pollRef.current === controller) pollRef.current = null;
        });
    },
    [abort],
  );

  // `create` performs the POST and resolves with the job; the caller has
  // already dispatched SUBMIT.
  const start = useCallback(
    (create: () => Promise<T>) => {
      create()
        .then((job) => {
          if (!activeRef.current) return;
          latest.current.dispatch({ type: "JOB_CREATED", jobId: job.id });
          runPoll(job.id);
        })
        .catch((error) => {
          if (!activeRef.current) return;
          toastResponseError(error);
          latest.current.dispatch({ type: "JOB_CREATE_FAILED" });
        });
    },
    [runPoll],
  );

  const keepWaiting = useCallback(
    (jobId: number) => {
      latest.current.dispatch({ type: "KEEP_WAITING" });
      runPoll(jobId);
    },
    [runPoll],
  );

  return { start, keepWaiting, abort, stop };
};

export default useAgentJobRun;
